import React, { createContext, useContext, useMemo, useState } from "react"
import PropTypes from "prop-types"

/**
 * Coordinates hover popovers so only one is ever open across every calendar
 * view. Each popover asks to open by id; opening one closes any other.
 * Context is null when no provider is mounted so consumers can fall back to
 * purely local open state.
 */
const BookingPopoverContext = createContext(null)

export const BookingPopoverProvider = ({ children }) => {
  const [openId, setOpenId] = useState(null)

  const value = useMemo(
    () => ({
      openId,
      requestOpen: (id) => setOpenId(id),
      close: (id) => setOpenId((current) => (id == null || current === id ? null : current))
    }),
    [openId]
  )

  return <BookingPopoverContext.Provider value={value}>{children}</BookingPopoverContext.Provider>
}

BookingPopoverProvider.propTypes = {
  children: PropTypes.node
}

/**
 * Returns { isOpen, requestOpen, close } for a popover identified by `id`.
 * Uses the shared provider when present (single-open guarantee), otherwise
 * degrades to isolated local state.
 */
export const useBookingPopover = (id) => {
  const shared = useContext(BookingPopoverContext)
  const [localOpen, setLocalOpen] = useState(false)

  if (shared) {
    return {
      isOpen: shared.openId === id,
      requestOpen: () => shared.requestOpen(id),
      close: () => shared.close(id)
    }
  }

  return {
    isOpen: localOpen,
    requestOpen: () => setLocalOpen(true),
    close: () => setLocalOpen(false)
  }
}

export default BookingPopoverContext
